<template>
	<div>
		<handy-scroll class="handscrolls" ref="handscrolls">
			<AgencyEvaluationSummaryTable
				class="agency-contest-evaluation-table"
				:table-name="viewTitle"
				:headers="evaluationsData.headers"
				:items="evaluationsData.items"
				:row-toolbar-actions-visible="false"
				:hide-pagination="true"
				:hideable-columns="true"
				:export-enabled="true"
				@item-updated="updateSummary"
				compact
			></AgencyEvaluationSummaryTable>
		</handy-scroll>
	</div>
</template>

<script>
	import {Team} from "@/api/generated/Team";
	import {AgencyContestEvaluationSummary} from "@/api/generated/AgencyContestEvaluationSummary";
	import HandyScroll, {EventBus} from "vue-handy-scroll";
	import AgencyEvaluationSummaryTable from "./components/AgencyEvaluationSummaryTable";

	export default {
		name: "AgencyContestEvaluationSummaryView",

		components: {AgencyEvaluationSummaryTable, HandyScroll},

		data() {
			return {
				agencyContestData: {
					id: this.$route.params.id,
				},
				teamData: {
					id: this.$route.params.teamId,
					name: "",
				},
				evaluationsData: {
					items: [],
					headers: [],
				},
			};
		},

		watch: {
			evaluationsData () {
				EventBus.$emit("update");
			}
		},

		computed: {
			viewTitle() {
				if (this.teamData.id) {
					return "Zbiorcza ocena projektów w zespole \"" + this.teamData.name + "\"";
				} else {
					return "Zbiorcza ocena projektów w naborze";
				}
			},
		},

		created() {
			this.reinitialize();
		},

		methods: {
			reinitialize() {
				if (this.teamData.id) {
					loadTeamData(this);
				}
				loadProjects(this);
			},

			updateSummary(summary) {
				if (!summary.recommendedDotation) {
					this.$messagesBus.$emit("error", "Pole kwota rekomendowana jest wymagane");
					return;
				}
				processDynamicValues(summary, this.evaluationsData.headers);
				AgencyContestEvaluationSummary.put(this, summary,
					success => {
						this.$messagesBus.$emit("success", "Zapisano zmiany", success);
						loadProjects(this);
					},
					error => {
						this.$messagesBus.$emit("error", "Nie udało się wprowadzić zmian", error);
					});
			},
		},
	};

	function processDynamicValues(summary, headers) {
		const endsWithNumber = new RegExp("(.*)(\\d+)");
		headers.forEach(header => {
			if (header.editable && endsWithNumber.test(header.value)) {
				const result = endsWithNumber.exec(header.value);
				summary[result[1]][result[2] - 1] = summary[header.value];
			}
		});
	}

	function loadProjects(ctx) {
		let successHandler = (successResponse) => {
			ctx.evaluationsData.headers = processItemsAndBuildHeaders(ctx, successResponse);
			ctx.evaluationsData.items = successResponse;
			EventBus.$emit("update");
		};
		let errorHandler = (errorResponse) => {
			ctx.$messagesBus.$emit("error", "Nie udało się pobrać zbiorczej oceny", errorResponse);
		};

		if (ctx.teamData.id) {
			AgencyContestEvaluationSummary.getTeam(ctx, ctx.teamData.id, successHandler, errorHandler);
		} else {
			AgencyContestEvaluationSummary.get(ctx, ctx.agencyContestData.id, successHandler, errorHandler);
		}
	}

	function loadTeamData(ctx) {
		Team.get(ctx, ctx.teamData.id,
			(success) => {
				ctx.teamData = success;
			}, (error) => {
				ctx.$messagesBus.$emit("error", "Nie udało się pobrać danych zespołu", error);
			});
	}

	function processItemsAndBuildHeaders(ctx, summaries) {
		let h = [];
		h.push(
			buildHeader("Lp.", "index", {s: false, f: false, t: "NUMBER", tooltip: "Liczba porządkowa"}),
			buildHeader("Nr wniosku", "projectCode", {tooltip: "Numer wniosku"}),
			buildHeader("Placówka", "agencyName"),
			buildHeader("Tytuł wniosku", "title"),
			buildHeader("Kraj", "accreditationCountryName"),
			buildHeader("Wnioskowana kwota", "requestedDotation", {t: "CURRENCY", su: true}),
		);

    for (let item of summaries) {
			appendEvaluatorNameMultiHeader(ctx, h, item);
		}

    const substantiveEvaluationsRecommendingsColumnCount = Math.max(...summaries.map(offer => offer.substantiveEvaluationsRecommendings ? offer.substantiveEvaluationsRecommendings.length : 0));
    const substantiveEvaluationsPointSumsColumnCount = Math.max(...summaries.map(offer => offer.substantiveEvaluationsPointSums ? offer.substantiveEvaluationsPointSums.length : 0));

    for (let i = 0; i < summaries.length; i++) {
			let s = summaries[i];
			if (isSubstantiveEvaluationBinary(s)) {
				appendMultiHeader(ctx, h, s, "Ocena", "substantiveEvaluationsRecommendings", {t: "BOOLEAN", ic: " O"}, substantiveEvaluationsRecommendingsColumnCount);
			} else {
				appendMultiHeader(ctx, h, s, "Ocena", "substantiveEvaluationsPointSums", {t: "NUMBER", ic: " O"}, substantiveEvaluationsPointSumsColumnCount);
			}
		}

		// Dodaj kolumny z nazwiskami ekspertów
    for (let item of summaries) {
			appendExpertNameMultiHeader(ctx, h, item);
		}

		// Dodaj kolumny z opiniami eksperckimi
		const expertEvaluationsColumnCount = Math.max(...summaries.map(offer => offer.expertEvaluations ? offer.expertEvaluations.length : 0));
    for (let item of summaries) {
			appendMultiHeader(ctx, h, item, "Opinia", "expertEvaluations", {t: "BOOLEAN", ic: " E"}, expertEvaluationsColumnCount);
		}

		h.push(
			buildHeader("Kwota rekomendowana", "recommendedDotation", {t: "CURRENCY", e: true, su: true,
				pp: (s) => {
					if (isNaN(s["recommendedDotation"]) === true || s["recommendedDotation"] === null) {
						return parseFloat(s["requestedDotation"]);
					}
					return parseFloat(s["recommendedDotation"]);
				}}),
      buildHeader("Waluta", "currency"),
			buildHeader("Redukcja", "dotationReduction", {
				t: "CURRENCY",
				su: true,
				pp: (s) => (parseInt(s["requestedDotation"]) || 0) - (parseInt(s["recommendedDotation"]) || 0),
			}),
		);

		return h;
	}

	function appendMultiHeader(ctx, headers, item, rootText, rootKey, o = {}, columnCount) {
		const indexCaption = typeof o.ic === "undefined" ? "" : o.ic;

		for (let j = 0; j < item[rootKey].length; j++) {
			let key = rootKey + (j + 1);

			ctx.$set(item, key, item[rootKey][j]);

			if (!headers.some(h => h.value === key)) {
				let text = rootText + (columnCount > 1 ? indexCaption + (j + 1) : "");

				let singularOptions = Object.assign({}, o);
				singularOptions.pp = typeof o.pp === "function" ? o.pp(j + 1) : null;

				headers.push(buildHeader(text, key, singularOptions));
			}
		}
	}

	function buildHeader(text, value, o = {}) {
		return {
			text: text,
			value: value,
			sortable: typeof o.s === "undefined" ? true : o.s,
			filterable: typeof o.f === "undefined" ? true : o.f,
			editable: typeof o.e === "undefined" ? false : o.e,
			summable: typeof o.su === "undefined" ? false : o.su,
			tooltip: typeof o.tooltip === "undefined" ? false : o.tooltip,
			type: o.t ? o.t : "TEXT",
			postProcessor: o.pp,
			headerClass: "header-" + value,
			cellClass: "cell-" + value,
		};
	}

	function appendExpertNameMultiHeader(ctx, headers, item) {
		if (!item.expertNames) return;
		for (let j = 0; j < item.expertNames.length; j++) {
			let key = "expertNames" + (j + 1);

			ctx.$set(item, key, item.expertNames[j]);

			if (!headers.some(h => h.value === key)) {
				let text = "Ekspert" + (item.expertNames.length > 1 ? " " + (j + 1) + " (E" + (j + 1) + ")" : "");

				headers.push(buildHeader(text, key, {}));
			}
		}
	}

	function appendEvaluatorNameMultiHeader(ctx, headers, item) {
		if (!item.committeeNames) return;
		for (let j = 0; j < item.committeeNames.length; j++) {
			let key = "committeeNames" + (j + 1);

			ctx.$set(item, key, item.committeeNames[j]);

			if (!headers.some(h => h.value === key)) {
				let text = "Opiniujący" + (item.committeeNames.length > 1 ? " " + (j + 1) + " (O" + (j + 1) + ")" : "");

				headers.push(buildHeader(text, key, {}));
			}
		}
	}

	function isSubstantiveEvaluationBinary(item) {
		return item.substantiveEvaluationsRecommendings;
	}
</script>

<style>
	.agency-contest-evaluation-table {
		display: inline-block;
		min-width: 100%;
	}

	.compact-table .header-projectCode {
		max-width: 150px;
	}
</style>
